import {
    isValidElement,
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type IAccordionProps from "./type";
import type {
    AccordionNavigateConstantType,
    AccordionTriggerEntries,
    AccordionTriggerEntry,
    AccordionContextType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import AccordionContent from "./components/content";
import AccordionItem from "./components/item";
import AccordionTrigger from "./components/trigger";
import {
    AccordionLayoutContext,
    AccordionContext
} from "./context";
import {
    getAccordionOrderedTriggers,
    getAccordionNavigationIndex,
    getHasAccordionExtraValues,
    ACCORDION_REGION_MAX_ITEMS,
    getAccordionChangeValue,
    getAccordionNextValues,
    getAccordionValues,
    getAccordionLayout
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    warnOnce
} from "../../utils/development";

const Accordion: FC<IAccordionProps> = ({
    expandBehaviour = "single",
    isCollapsible = true,
    isDisabled = false,
    variant = "plain",
    value: valueProp,
    size = "medium",
    onValueChange,
    defaultValue,
    initialValue,
    customTheme,
    children,
    onChange,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const triggersRef = useRef<AccordionTriggerEntries>(new Map());

    const [
        valueState,
        setValueState
    ] = useState<Array<string>>(() => getAccordionValues({
        value: defaultValue ?? initialValue,
        expandBehaviour
    }));

    const isControlled = valueProp !== undefined;

    const openValues = useMemo(() => getAccordionValues({
        value: isControlled ? valueProp : valueState,
        expandBehaviour
    }), [
        isControlled,
        expandBehaviour,
        valueState,
        valueProp
    ]);

    const layout = getAccordionLayout({
        children
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    useEffect(() => {
        if(!isControlled || onChange || onValueChange) {
            return;
        }

        warnOnce({
            message: "Accordion: a controlled \"value\" needs an \"onValueChange\" handler that updates it."
        });
    }, [
        onValueChange,
        isControlled,
        onChange
    ]);

    useEffect(() => {
        if(initialValue === undefined) {
            return;
        }

        warnOnce({
            message: "Accordion: \"initialValue\" is deprecated and will be removed in the next major version. Use \"defaultValue\" instead."
        });
    }, [
        initialValue
    ]);

    useEffect(() => {
        if(onChange === undefined) {
            return;
        }

        warnOnce({
            message: "Accordion: \"onChange\" is deprecated and will be removed in the next major version. Use \"onValueChange\" instead."
        });
    }, [
        onChange
    ]);

    useEffect(() => {
        if(!getHasAccordionExtraValues({
            value: valueProp ?? defaultValue ?? initialValue,
            expandBehaviour
        })) {
            return;
        }

        warnOnce({
            message: "Accordion: \"expandBehaviour\" is \"single\", so only the first of the given values is used."
        });
    }, [
        expandBehaviour,
        defaultValue,
        initialValue,
        valueProp
    ]);

    const registerTrigger = useCallback((id: string, entry: AccordionTriggerEntry) => {
        triggersRef.current.set(id, entry);

        return () => {
            triggersRef.current.delete(id);
        };
    }, []);

    const handleNavigate = useStableCallback(({
        key,
        id
    }: AccordionNavigateConstantType) => {
        const orderedTriggers = getAccordionOrderedTriggers({
            triggers: triggersRef.current
        });

        const nextTrigger = orderedTriggers[getAccordionNavigationIndex({
            current: orderedTriggers.findIndex((trigger) => trigger.id === id),
            count: orderedTriggers.length,
            key
        })];

        if(!nextTrigger) {
            return false;
        }

        nextTrigger.ref.current?.focus();

        return true;
    });

    const handleToggle = useStableCallback((itemValue: string) => {
        const nextValues = getAccordionNextValues({
            expandBehaviour,
            isCollapsible,
            openValues,
            itemValue
        });

        if(!nextValues) {
            return;
        }

        if(!isControlled) {
            setValueState(nextValues);
        }

        const changeValue = getAccordionChangeValue({
            values: nextValues,
            expandBehaviour
        });

        onValueChange?.(changeValue);

        onChange?.(changeValue);
    });

    const isRegionEnabled = layout.itemCount <= ACCORDION_REGION_MAX_ITEMS;

    const context = useMemo<AccordionContextType>(() => ({
        expandBehaviour,
        isRegionEnabled,
        registerTrigger,
        handleNavigate,
        isCollapsible,
        handleToggle,
        customTheme,
        isDisabled,
        openValues,
        variant,
        size
    }), [
        expandBehaviour,
        registerTrigger,
        isRegionEnabled,
        handleNavigate,
        isCollapsible,
        handleToggle,
        customTheme,
        isDisabled,
        openValues,
        variant,
        size
    ]);

    return <AccordionContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {
                layout.items.map(({
                    layout: itemLayout,
                    node
                }, index) => <AccordionLayoutContext.Provider
                    key={isValidElement(node) ? node.key ?? index : index}
                    value={itemLayout}
                >
                    {node}
                </AccordionLayoutContext.Provider>)
            }
        </View>
    </AccordionContext.Provider>;
};
export default Object.assign(Accordion, {
    Content: AccordionContent,
    Trigger: AccordionTrigger,
    Item: AccordionItem
});
